/**
 * Prescriptions and doctor pharmacy modules.
 */
import { fetchApi, API_BASE_URL } from '../client';

const APP_ENV = (import.meta.env.VITE_APP_ENV as string | undefined) ?? (import.meta.env.DEV ? 'dev' : 'prod');
import { secureStorage } from '../../secureStorage';
import { safeConsole } from '../../piiProtection';
import { getFriendlyErrorMessage } from '../../errorMapping';
import type { Prescription, PrescriptionCreateRequest } from '../../../types';
import type { PagedResponse } from '../../../types/pagination';

export interface MedicationSendDecision {
  prescriptionMedicationId: string;
  decision: 'AS_PRESCRIBED' | 'SUBSTITUTE' | 'SEND_ANYWAY';
  substitutePharmacyMedicationId?: string;
  substituteMedicationName?: string;
}

export interface SubstituteOption {
  pharmacyMedicationId: string;
  medicationName: string;
  currentQuantity: number | null;
}

export interface MedicationAvailabilityItem {
  prescriptionMedicationId: string;
  medicationName: string;
  matchType: 'EXACT_MATCH' | 'UNAVAILABLE' | 'UNKNOWN';
  currentQuantity: number | null;
  resolvedGenericName: string | null;
  substitutes: SubstituteOption[];
}

export interface PharmacyAvailabilityResponse {
  pharmacyId: string;
  pharmacyName: string;
  inventoryTracked: boolean;
  items: MedicationAvailabilityItem[];
}

export const prescriptionsModule = {
  getByPatient: (patientId: string) =>
    fetchApi<Prescription[]>(`/api/doctor/prescriptions?patientId=${patientId}`),

  /** One page of a patient's prescriptions, newest first, paged in SQL (medopd-docs 112). */
  getByPatientPage: (patientId: string, page: number, size: number) =>
    fetchApi<PagedResponse<Prescription>>(`/api/doctor/prescriptions?patientId=${encodeURIComponent(patientId)}&page=${page}&size=${size}`),

  getByVisit: (visitId: string) =>
    fetchApi<Prescription[]>(`/api/doctor/prescriptions?visitId=${visitId}`),

  getById: (id: string) =>
    fetchApi<Prescription>(`/api/doctor/prescriptions/${id}`),

  create: (data: PrescriptionCreateRequest) =>
    fetchApi<Prescription>('/api/doctor/prescriptions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateDraft: (id: string, data: any) =>
    fetchApi<any>(`/api/doctor/prescriptions/${id}/draft`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  issue: (id: string) =>
    fetchApi<Prescription>(`/api/doctor/prescriptions/${id}/issue`, { method: 'POST' }),

  cancel: (id: string, reason?: string) =>
    fetchApi<Prescription>(`/api/doctor/prescriptions/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  sendToPharmacy: (id: string, pharmacyId: string, notes?: string, medicationDecisions?: MedicationSendDecision[]) =>
    fetchApi<any>(`/api/doctor/prescriptions/${id}/send-to-pharmacy`, {
      method: 'POST',
      body: JSON.stringify({ pharmacyId, notes, medicationDecisions }),
    }),

  /** Stock-aware prescribing: per-medication availability + generic-substitute suggestions at a
   *  candidate pharmacy, called before confirming send-to-pharmacy. Never blocks sending. */
  getPharmacyAvailability: (prescriptionId: string, pharmacyId: string) =>
    fetchApi<PharmacyAvailabilityResponse>(
      `/api/doctor/prescriptions/${prescriptionId}/pharmacy-availability?pharmacyId=${encodeURIComponent(pharmacyId)}`
    ),

  /** Share an issued prescription with the patient via WhatsApp/SMS (secure link) and/or Email
   *  (PDF attached + link). Returns per-channel SENT/FAILED so the UI can show exactly what worked. */
  share: (id: string, channels: Array<'WHATSAPP' | 'SMS' | 'EMAIL'>) =>
    fetchApi<Record<string, { channel: string; success: boolean; error: string | null }>>(
      `/api/doctor/prescriptions/${id}/share`,
      { method: 'POST', body: JSON.stringify({ channels }) }
    ),

  getPharmacyDelivery: (id: string) =>
    fetchApi<any>(`/api/doctor/prescriptions/${id}/pharmacy-delivery`),

  cancelPharmacyDelivery: (id: string, reason?: string) =>
    fetchApi<any>(`/api/doctor/prescriptions/${id}/cancel-pharmacy-delivery`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  getByAppointment: (appointmentId: string) =>
    fetchApi<any>(`/api/prescriptions/appointment/${appointmentId}`),

  reissue: (data: any) =>
    fetchApi<any>('/api/prescriptions/reissue', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getAuditTrail: (prescriptionId: string) =>
    fetchApi<any>(`/api/prescriptions/${prescriptionId}/audit`),

  downloadPdf: async (id: string, _userRole?: string) => {
    const token = secureStorage.getAuthToken();
    const headers: Record<string, string> = {
      'X-Client-Version': '1.0.0',
      'X-App-Environment': APP_ENV,
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/doctor/prescriptions/${id}/download-pdf`,
        { method: 'GET', headers, credentials: 'include' }
      );
      if (!response.ok) {
        if (response.status === 401) {
          secureStorage.removeAuthToken();
          window.location.href = '/login';
          throw new Error('Unauthorized');
        }
        throw new Error('Failed to download PDF');
      }
      const blob = await response.blob();
      const cd = response.headers.get('content-disposition');
      const fileName = (cd?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/) ?? [])[1]?.replace(/['"]/g, '') || `prescription-${id}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      return { data: { success: true } };
    } catch (error) {
      safeConsole.error('Error downloading PDF:', error);
      return { error: getFriendlyErrorMessage(error instanceof Error ? error.message : 'Failed to download PDF') };
    }
  },
};

/** Doctor-scoped pharmacy list (not the full pharmacy admin). */
export const pharmaciesModule = {
  getAll: (activeOnly = false) =>
    fetchApi<any>(`/api/doctor/pharmacies?activeOnly=${activeOnly}`),

  getById: (id: string) =>
    fetchApi<any>(`/api/doctor/pharmacies/${id}`),

  create: (data: any) =>
    fetchApi<any>('/api/doctor/pharmacies', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: any) =>
    fetchApi<any>(`/api/doctor/pharmacies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  delete: (id: string) =>
    fetchApi<any>(`/api/doctor/pharmacies/${id}`, { method: 'DELETE' }),

  updateStatus: (id: string, status: string) =>
    fetchApi<any>(`/api/doctor/pharmacies/${id}/status?status=${status}`, { method: 'PATCH' }),
};
